import { StatsigClient } from "@statsig/react-bindings";
import { startRecording, stopRecording } from "@statsig/session-replay";
import { debug } from "@/utils/debug";
import { FEATURE_GATES, type FeatureGateName } from "@/utils/feature-gates";

let statsigClient: StatsigClient | null = null;

export const setStatsigClient = (client: StatsigClient): void => {
  debug.log("[statsig] setStatsigClient called");
  statsigClient = client;
};

export const getStatsigClient = (): StatsigClient | null => {
  return statsigClient;
};

export const checkStatsigGate = (gateName: FeatureGateName): boolean => {
  if (!statsigClient) {
    debug.warn(
      "[statsig] checkStatsigGate — client is null, returning false for",
      gateName
    );
    return false;
  }
  const result = statsigClient.checkGate(gateName);
  debug.log("[statsig] checkStatsigGate", gateName, "→", result);
  return result;
};

/**
 * Returns `null` when the Statsig client is not yet initialised (indeterminate),
 * so callers can distinguish "gate OFF" from "we don't know yet".
 */
export const checkEntraAuthGate = (): boolean | null => {
  if (!statsigClient) {
    debug.warn(
      "[statsig] checkEntraAuthGate — client is null, returning null (indeterminate)"
    );
    return null;
  }
  const result = statsigClient.checkGate(FEATURE_GATES.AUTH_VIA_ENTRA_ID);
  debug.log("[statsig] checkEntraAuthGate →", result);
  return result;
};

export const logStatsigEvent = (
  eventName: string,
  value?: string | number,
  metadata?: Record<string, string>
): void => {
  if (!statsigClient) {
    debug.warn(
      "[statsig] logStatsigEvent — client is null, skipping event:",
      eventName
    );
    return;
  }
  debug.log("[statsig] logStatsigEvent", eventName, value, metadata);
  statsigClient.logEvent(eventName, value, metadata);
};

/** @see https://docs.statsig.com/session-replay/configure#advanced-forcing-a-recording-on-demand */
export const startStatsigRecording = (): void => {
  debug.log("[statsig] startStatsigRecording");
  startRecording(import.meta.env.VITE_STATSIG_CLIENT_KEY);
};

/** @see https://docs.statsig.com/session-replay/configure#advanced-forcing-a-recording-on-demand */
export const stopStatsigRecording = (): void => {
  debug.log("[statsig] stopStatsigRecording");
  stopRecording(import.meta.env.VITE_STATSIG_CLIENT_KEY);
};
